/*
 * ******************************************************************
 * Copyright (c) 2024-2026 Broadcom. All Rights Reserved.
 * Broadcom Confidential. The term "Broadcom" refers to Broadcom Inc.
 * and/or its subsidiaries.
 * ******************************************************************
 */

import { AfterViewInit, Component, ElementRef, Input, OnChanges, SimpleChanges, ViewChild } from "@angular/core";

import Highcharts from "highcharts";
import 'highcharts/modules/accessibility';
import { ThemeService } from "@app/services";

// In Highcharts 12, `highcharts/themes/*` master entries export the Highcharts
// instance (not the theme namespace) and auto-apply on import, so .apply() no
// longer exists on the default export. The ES-module implementations export the
// theme namespace { options, apply() } and do not auto-apply.
// eslint-disable-next-line @typescript-eslint/no-require-imports
const darkTheme = require("highcharts/es-modules/Extensions/Themes/HighContrastDark").default as { apply: () => void };
// eslint-disable-next-line @typescript-eslint/no-require-imports
const lightTheme = require("highcharts/es-modules/Extensions/Themes/HighContrastLight").default as { apply: () => void };

@Component({
    standalone: false,
    selector: 'horizontal-bar-chart',
    templateUrl: './horizontal-bar-chart.component.html',
    styleUrls: ['./horizontal-bar-chart.component.scss'],
})
export class HorizontalBarChartComponent implements AfterViewInit, OnChanges {

    @ViewChild('chartRef') chartRef: ElementRef;

    @Input() title: string;
    @Input() categories: any;
    @Input() series: Highcharts.SeriesOptionsType[];


    constructor(private themeService: ThemeService) {
        if (this.themeService.isDarkTheme()) {
            darkTheme.apply();
        } else {
            lightTheme.apply();
        }
    }

    ngAfterViewInit(): void {
        this.drawChart();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['series']?.previousValue != undefined && changes['series'].currentValue != changes['series'].previousValue) {
            this.drawChart();
        }
    }

    private drawChart(): void {
        // @ts-expect-error javascript callback
        Highcharts.chart(this.chartRef.nativeElement, {
            chart: {
                type: 'column',
                backgroundColor: 'transparent'
            },
            title: {
                text: this.title ? this.title : null,
                align: 'left'
            },
            xAxis: {
                categories: this.categories,
                visible: true,
                gridLineWidth: 0,
                lineWidth: 0,
                tickWidth: 0,
                min: null
            },
            yAxis: [{
                min: 0,
                title: {
                    enabled: false,
                }
            }],
            legend: {
                events: {
                    itemClick: _event => {
                        return false
                    }
                }
            },
            tooltip: {
                shared: true
            },
            credits: {
                enabled: false
            },
            accessibility: {
                enabled: true
            },
            plotOptions: {
                column: {
                    shadow: false,
                    borderWidth: 0
                },
                series: {
                    stacking: 'normal'
                }
            },
            series: this.series ? this.series : []
        });
    }
}
